/**
 * Dynamic SEO & Metadata Engine for SquareMagnets.ca
 * Generates route-specific titles, descriptions, open graph tags,
 * canonical links, and dynamic JSON-LD schemas (Organization, LocalBusiness, Product, FAQPage)
 * using real-time admin pricing configurations.
 */

import { useEffect } from 'react';
import { PricingConfig, ScaledDiscount } from '../types';
import { USE_CASE_PAGES, isUseCaseSlug } from '../data/useCasePages';

export interface RouteSeoConfig {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: 'summary' | 'summary_large_image';
}

const DEFAULT_ORIGIN = 'https://squaremagnets.ca';
const DEFAULT_IMAGE = 'https://squaremagnets.ca/custom-magnets.webp';

export function getPriceSummary(pricing?: Partial<PricingConfig> | null) {
  const basePrice = typeof pricing?.pricePerMagnet === 'number' ? pricing.pricePerMagnet : 5.99;
  const shippingRate = typeof pricing?.shippingRate === 'number' ? pricing.shippingRate : 10.00;
  const freeShippingThreshold = typeof pricing?.freeShippingThreshold === 'number' ? pricing.freeShippingThreshold : 75.00;
  const scaledDiscounts: ScaledDiscount[] = Array.isArray(pricing?.scaledDiscounts) && pricing!.scaledDiscounts.length > 0
    ? pricing!.scaledDiscounts
    : [
        { minQuantity: 2, pricePerMagnet: 5.59 },
        { minQuantity: 6, pricePerMagnet: 5.49 },
        { minQuantity: 9, pricePerMagnet: 5.25 },
        { minQuantity: 12, pricePerMagnet: 4.99 },
        { minQuantity: 18, pricePerMagnet: 4.49 },
        { minQuantity: 24, pricePerMagnet: 3.99 },
        { minQuantity: 48, pricePerMagnet: 3.00 },
        { minQuantity: 250, pricePerMagnet: 2.49 }
      ];

  const sortedDiscounts = [...scaledDiscounts].sort((a, b) => a.minQuantity - b.minQuantity);
  const lowestPrice = sortedDiscounts.length > 0
    ? Math.min(...sortedDiscounts.map(d => d.pricePerMagnet))
    : basePrice;

  const maxSavingsPercent = Math.round(((basePrice - lowestPrice) / basePrice) * 100);

  const kamloopsRate = typeof pricing?.kamloopsShippingRate === 'number' ? pricing.kamloopsShippingRate : 5.00;
  const localCity = pricing?.localDeliveryCityName || 'Kamloops';

  return {
    basePrice,
    lowestPrice,
    shippingRate,
    freeShippingThreshold,
    scaledDiscounts: sortedDiscounts,
    maxSavingsPercent,
    kamloopsRate,
    localCity,
    enableKamloopsDiscount: pricing?.enableKamloopsDiscount ?? true,
    enableFreeShipping: pricing?.enableFreeShipping ?? false
  };
}

export function getRouteSeo(
  activeTab: string,
  params: {
    selectedNewsSlug?: string | null;
    newsTitle?: string | null;
    newsSummary?: string | null;
    pricing?: Partial<PricingConfig> | null;
    dashboardTab?: string;
  }
): RouteSeoConfig {
  const { lowestPrice, freeShippingThreshold, maxSavingsPercent, enableFreeShipping } = getPriceSummary(params.pricing);
  const freeShippingPhrase = enableFreeShipping
    ? 'free Canada-wide shipping on all orders'
    : `free shipping over $${freeShippingThreshold.toFixed(2)} CAD`;

  switch (activeTab) {
    case 'shop':
    case 'studio':
      return {
        title: `Design Custom Photo Magnets Online | SquareMagnets.ca Design Studio`,
        description: `Create personalized 2.5″ × 2.5″ square photo magnets from $${lowestPrice.toFixed(2)} CAD. Live 3D preview, filters, multi-photo crop, custom captions, and ${freeShippingPhrase}.`,
        keywords: 'design custom magnets, photo fridge magnets, custom logo magnets, personalized magnet studio, bulk magnets Canada, Kamloops custom magnets',
        canonicalPath: '/studio',
        ogTitle: `Custom Magnet Design Studio | Live 3D Preview | SquareMagnets.ca`,
        ogDescription: `Upload phone photos or logos, add custom text with 18+ fonts, and order glossy handcrafted square magnets. Bulk savings up to ${maxSavingsPercent}%!`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'moments':
      return {
        title: `Moments Creator – Custom Collage & Keepsake Magnets | SquareMagnets.ca`,
        description: `Design timeless collage photo magnets for weddings, family milestones, babies, and travel memories. Starting at $${lowestPrice.toFixed(2)} CAD with ${freeShippingPhrase}.`,
        keywords: 'custom collage magnets, wedding favor magnets, baby milestone keepsakes, anniversary photo magnets, family memory fridge magnets',
        canonicalPath: '/moments',
        ogTitle: `Moments Creator – Personalized Collage Magnets | SquareMagnets.ca`,
        ogDescription: `Turn special highlights, wedding celebrations, and vacations into beautiful multi-photo keepsake magnets. Handcrafted in Canada.`,
        ogImage: 'https://squaremagnets.ca/memory-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'fundraising':
      return {
        title: `School & Sports Team Magnet Fundraiser (Earn up to 30%) | SquareMagnets.ca`,
        description: `Zero upfront cost custom photo magnet fundraiser for schools, clubs, and sports teams across Canada. Earn up to 30% of sales with participant attribution and direct delivery.`,
        keywords: 'school fundraising Canada, sports team fundraiser, photo magnet fundraiser, high margin fundraising, zero upfront cost fundraising, Kamloops fundraising',
        canonicalPath: '/fundraising',
        ogTitle: `High-Return Magnet Fundraising for Schools & Teams | SquareMagnets.ca`,
        ogDescription: `No paper order forms, no perishable goods. Families design custom photo magnets online, and your group earns steady fundraising revenue.`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'giftcards':
    case 'gift-cards':
      return {
        title: `Digital Gift Cards ($30 to $1,000 CAD) | Give the Gift of Custom Magnets | SquareMagnets.ca`,
        description: `Send an instant SquareMagnets.ca Digital Gift Card with a personal message. Recipients can design their own 2.5″ × 2.5″ photo magnets whenever they're ready with ${freeShippingPhrase}. Never expires.`,
        keywords: 'custom magnet gift card, photo gift card Canada, digital gift certificate, personalized photo magnet gift, SquareMagnets gift code',
        canonicalPath: '/gift-cards',
        ogTitle: `SquareMagnets.ca Digital Gift Cards | Instant Delivery & Never Expires`,
        ogDescription: `Give friends and family the freedom to turn their favourite camera roll photos into handcrafted 2.5″ × 2.5″ square magnets.`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'news':
      if (params.selectedNewsSlug && params.newsTitle) {
        return {
          title: `${params.newsTitle} | SquareMagnets.ca Blog`,
          description: params.newsSummary || `Read ${params.newsTitle} on SquareMagnets.ca. Tips, gift guides, and creative ideas for custom photo and promotional magnets.`,
          keywords: 'custom magnet blog, photo keepsake tips, promotional magnet marketing, gift guides, Kamloops BC',
          canonicalPath: `/news/${params.selectedNewsSlug}`,
          ogTitle: `${params.newsTitle} | SquareMagnets.ca`,
          ogDescription: params.newsSummary || 'Custom magnet inspiration, customer stories, and manufacturing updates.',
          ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
          twitterCard: 'summary_large_image'
        };
      }
      return {
        title: `Magnet Inspiration, Gift Ideas & Design Guides | SquareMagnets.ca Blog`,
        description: `Explore tips on choosing the best photos, business promotional ideas, wedding favor inspiration, and how custom fridge magnets are made in Kamloops, BC.`,
        keywords: 'custom magnet guides, fridge magnet ideas, wedding favor magnets blog, business branding tips, Canadian print studio',
        canonicalPath: '/news',
        ogTitle: `SquareMagnets.ca Blog – Ideas, Guides & Keepsake Inspiration`,
        ogDescription: `Helpful articles, design tips, and creative inspiration for turning your favourite photos and logos into premium square magnets.`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'cart':
      return {
        title: `Shopping Cart & Checkout | SquareMagnets.ca`,
        description: `Review your custom square magnet designs, apply automatic bulk quantity discounts up to ${maxSavingsPercent}%, and checkout securely with Stripe (${freeShippingPhrase}).`,
        keywords: 'shopping cart, custom magnet checkout, bulk discount magnets, Stripe secure checkout',
        canonicalPath: '/cart',
        ogTitle: `Your SquareMagnets.ca Shopping Cart`,
        ogDescription: `Complete your custom magnet order with ${freeShippingPhrase}.`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary'
      };

    case 'dashboard':
      return {
        title: `Order Tracking & Magnet Proofs | SquareMagnets.ca`,
        description: `Track your custom magnet production status, view shipping tracking numbers, and access your saved designs in real time.`,
        keywords: 'order tracking, magnet shipping status, track package, saved magnet designs',
        canonicalPath: '/track',
        ogTitle: `Track Your Custom Magnet Order | SquareMagnets.ca`,
        ogDescription: `Real-time updates on your handcrafted custom magnet orders, prepress proofs, and Canada Post tracking.`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary'
      };

    case 'browse':
      return {
        title: `Curated Magnet Gallery & Funny Fridge Quotes | SquareMagnets.ca`,
        description: `Browse hundreds of pre-designed funny kitchen quotes, motivational signs, artistic prints, and meme magnets ready to ship across Canada.`,
        keywords: 'funny fridge magnets, funny kitchen quotes, ready-made magnets, meme magnets, artistic square magnets',
        canonicalPath: '/browse',
        ogTitle: `Browse Curated Magnet Designs | SquareMagnets.ca`,
        ogDescription: `Hilarious quotes, artistic patterns, and thoughtful gifts ready to add directly to your fridge.`,
        ogImage: 'https://squaremagnets.ca/funny-square-magnets.webp',
        twitterCard: 'summary_large_image'
      };

    case 'privacy':
      return {
        title: `Privacy Policy | SquareMagnets.ca`,
        description: `Learn how SquareMagnets.ca protects your personal information, photo assets, and transaction details with strict data security practices.`,
        keywords: 'privacy policy, customer data protection, secure image handling',
        canonicalPath: '/privacy',
        ogTitle: `Privacy Policy | SquareMagnets.ca`,
        ogDescription: `Our commitment to customer data privacy, image protection, and safe payments.`,
        ogImage: DEFAULT_IMAGE,
        twitterCard: 'summary'
      };

    case 'terms':
      return {
        title: `Terms of Service & Canadian Shipping Guarantee | SquareMagnets.ca`,
        description: `Terms and conditions for custom magnet orders, quality guarantees, returns policy, and copyright ownership guidelines.`,
        keywords: 'terms of service, refund policy, satisfaction guarantee, shipping policy',
        canonicalPath: '/terms',
        ogTitle: `Terms of Service | SquareMagnets.ca`,
        ogDescription: `Read our terms, conditions, quality promise, and shipping policies.`,
        ogImage: DEFAULT_IMAGE,
        twitterCard: 'summary'
      };

    case 'wedding-favors':
    case 'pet-magnets':
    case 'baby-milestones':
    case 'business-magnets':
    case 'memorial-magnets': {
      const uc = USE_CASE_PAGES[activeTab];
      return {
        title: uc.seoTitle,
        description: `${uc.seoDescription} Starting at $${lowestPrice.toFixed(2)} CAD with ${freeShippingPhrase}.`,
        keywords: uc.seoKeywords,
        canonicalPath: `/${uc.slug}`,
        ogTitle: uc.seoTitle,
        ogDescription: uc.seoDescription,
        ogImage: `${DEFAULT_ORIGIN}${uc.heroImage}`,
        twitterCard: 'summary_large_image'
      };
    }

    case 'home':
    default:
      return {
        title: `SquareMagnets.ca | Premium Custom Photo & Logo Magnets (Made in Canada)`,
        description: `Handcrafted 2.5″ × 2.5″ custom square photo & logo magnets from $${lowestPrice.toFixed(2)} CAD. Glossy finish, solid steel core, and ${freeShippingPhrase}. Made in Kamloops, BC.`,
        keywords: 'custom magnets, personalized photo magnets, custom square magnets, logo magnets Canada, bulk magnets, photo keepsakes, Kamloops custom magnets',
        canonicalPath: '/',
        ogTitle: `SquareMagnets.ca | Premium Custom Photo & Logo Magnets`,
        ogDescription: `Transform your favourite smartphone photos or business logos into durable, glossy 2.5″ square fridge magnets. Starting at $${lowestPrice.toFixed(2)} with ${freeShippingPhrase}!`,
        ogImage: 'https://squaremagnets.ca/custom-magnets.webp',
        twitterCard: 'summary_large_image'
      };
  }
}

/**
 * Builds the comprehensive dynamic JSON-LD structured data graph
 * incorporating Organization, WebSite, BreadcrumbList, LocalBusiness/Store, Product (with live price range & admin shipping config),
 * and FAQPage (with dynamic free shipping and bulk discount details).
 */
export function buildJsonLdGraph(pricing?: Partial<PricingConfig> | null, canonicalPath: string = '/') {
  const { basePrice, lowestPrice, freeShippingThreshold, shippingRate, kamloopsRate, localCity, scaledDiscounts, enableFreeShipping } = getPriceSummary(pricing);

  const canonicalUrl = `${DEFAULT_ORIGIN}${canonicalPath}`;
  const cleanSlug = canonicalPath.replace(/^\//, '');
  const matchedUseCase = isUseCaseSlug(cleanSlug) ? USE_CASE_PAGES[cleanSlug] : null;
  const tiersDescription = scaledDiscounts
    .map(d => `${d.minQuantity}+ at $${d.pricePerMagnet.toFixed(2)} each`)
    .join(', ');

  const deliveryTimeSpec = {
    '@type': 'ShippingDeliveryTime',
    'handlingTime': {
      '@type': 'QuantitativeValue',
      'minValue': 1,
      'maxValue': 2,
      'unitCode': 'DAY'
    },
    'transitTime': {
      '@type': 'QuantitativeValue',
      'minValue': 2,
      'maxValue': 5,
      'unitCode': 'DAY'
    }
  };

  const shippingDetailsSchema = enableFreeShipping
    ? {
        '@type': 'OfferShippingDetails',
        'shippingRate': {
          '@type': 'MonetaryAmount',
          'value': '0.00',
          'currency': 'CAD'
        },
        'shippingDestination': {
          '@type': 'DefinedRegion',
          'addressCountry': 'CA'
        },
        'deliveryTime': deliveryTimeSpec
      }
    : [
        {
          '@type': 'OfferShippingDetails',
          'shippingRate': {
            '@type': 'MonetaryAmount',
            'value': shippingRate.toFixed(2),
            'currency': 'CAD'
          },
          'shippingDestination': {
            '@type': 'DefinedRegion',
            'addressCountry': 'CA'
          },
          'deliveryTime': deliveryTimeSpec
        },
        {
          '@type': 'OfferShippingDetails',
          'name': `Free Canada-Wide Tracked Shipping on orders over $${freeShippingThreshold.toFixed(2)} CAD`,
          'shippingRate': {
            '@type': 'MonetaryAmount',
            'value': '0.00',
            'currency': 'CAD'
          },
          'freeShippingThreshold': {
            '@type': 'MonetaryAmount',
            'value': freeShippingThreshold.toFixed(2),
            'currency': 'CAD'
          },
          'shippingDestination': {
            '@type': 'DefinedRegion',
            'addressCountry': 'CA'
          },
          'deliveryTime': deliveryTimeSpec
        }
      ];

  const breadcrumbItems: any[] = [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': 'https://squaremagnets.ca/'
    }
  ];
  if (canonicalPath === '/studio') {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'Custom Magnet Studio', 'item': 'https://squaremagnets.ca/studio' });
  } else if (canonicalPath === '/moments') {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'Moments Collage Gallery', 'item': 'https://squaremagnets.ca/moments' });
  } else if (canonicalPath === '/fundraising') {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'School & Sports Team Fundraising', 'item': 'https://squaremagnets.ca/fundraising' });
  } else if (canonicalPath === '/gift-cards') {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'Digital Gift Cards', 'item': 'https://squaremagnets.ca/gift-cards' });
  } else if (canonicalPath === '/browse') {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'Curated Magnet Gallery', 'item': 'https://squaremagnets.ca/browse' });
  } else if (canonicalPath.startsWith('/news')) {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': 'Blog & Inspiration', 'item': 'https://squaremagnets.ca/news' });
  } else if (matchedUseCase) {
    breadcrumbItems.push({ '@type': 'ListItem', 'position': 2, 'name': matchedUseCase.navLabel, 'item': `https://squaremagnets.ca/${matchedUseCase.slug}` });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://squaremagnets.ca/#organization',
        'name': 'SquareMagnets.ca',
        'url': 'https://squaremagnets.ca/',
        'logo': 'https://squaremagnets.ca/squaremagnets-logo.webp',
        'contactPoint': {
          '@type': 'ContactPoint',
          'telephone': '1-250-320-3234',
          'contactType': 'customer service',
          'areaServed': 'CA',
          'availableLanguage': 'en'
        }
      },
      {
        '@type': 'WebSite',
        '@id': 'https://squaremagnets.ca/#website',
        'url': 'https://squaremagnets.ca/',
        'name': 'SquareMagnets.ca',
        'inLanguage': 'en-CA',
        'description': 'Premium Custom Square Magnet Design Tool & Boutique Printing Studio in Kamloops, BC',
        'publisher': {
          '@id': 'https://squaremagnets.ca/#organization'
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        'itemListElement': breadcrumbItems
      },
      {
        '@type': 'LocalBusiness',
        '@id': 'https://squaremagnets.ca/#localbusiness',
        'name': 'SquareMagnets.ca Custom Print Studio',
        'image': 'https://squaremagnets.ca/squaremagnets-logo.webp',
        'telephone': '1-250-320-3234',
        'email': 'contact@squaremagnets.ca',
        'url': 'https://squaremagnets.ca/',
        'priceRange': `$${lowestPrice.toFixed(2)} - $${basePrice.toFixed(2)} CAD`,
        'currenciesAccepted': 'CAD',
        'paymentAccepted': 'Credit Card, Debit Card, Apple Pay, Google Pay, Stripe',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Kamloops',
          'addressRegion': 'BC',
          'postalCode': 'V2C 1E9',
          'addressCountry': 'CA'
        },
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': '50.6745',
          'longitude': '-120.3273'
        },
        'areaServed': {
          '@type': 'Country',
          'name': 'Canada'
        },
        'openingHoursSpecification': [
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            'opens': '09:00',
            'closes': '17:00'
          }
        ],
        'makesOffer': [
          {
            '@type': 'Offer',
            'name': 'Free Canada-Wide Tracked Shipping',
            'description': enableFreeShipping
              ? 'Free standard tracked shipping across Canada on all orders'
              : `Free standard tracked shipping on all orders over $${freeShippingThreshold.toFixed(2)} CAD`,
            'price': '0.00',
            'priceCurrency': 'CAD'
          },
          {
            '@type': 'Offer',
            'name': 'Local Kamloops Delivery & Pickup',
            'description': `Special discounted local delivery in ${localCity}, BC for $${kamloopsRate.toFixed(2)} CAD`,
            'price': kamloopsRate.toFixed(2),
            'priceCurrency': 'CAD'
          }
        ]
      },
      {
        '@type': 'Product',
        '@id': 'https://squaremagnets.ca/#product',
        'name': 'Custom Square Photo & Logo Magnets (2.5 Inch x 2.5 Inch)',
        'sku': 'SQM-25X25-CUSTOM',
        'mpn': 'SQM-25X25-CA',
        'image': [
          'https://squaremagnets.ca/custom-magnets.webp',
          'https://squaremagnets.ca/memory-magnets.webp',
          'https://squaremagnets.ca/square-business-magnets.webp',
          'https://squaremagnets.ca/events-magnets.webp'
        ],
        'description': 'Fully personalizable high-resolution gloss-finish square magnets (2.5″ × 2.5″). Handcrafted in Canada with a solid steel shell core, extra-strength magnetic backing, and wipe-clean protective Mylar.',
        'brand': {
          '@type': 'Brand',
          'name': 'SquareMagnets.ca'
        },
        'countryOfOrigin': {
          '@type': 'Country',
          'name': 'CA'
        },
        'offers': {
          '@type': 'AggregateOffer',
          'priceCurrency': 'CAD',
          'lowPrice': lowestPrice.toFixed(2),
          'highPrice': basePrice.toFixed(2),
          'offerCount': '1000',
          'url': canonicalUrl,
          'itemCondition': 'https://schema.org/NewCondition',
          'availability': 'https://schema.org/InStock',
          'priceValidUntil': '2027-12-31',
          'hasMerchantReturnPolicy': {
            '@type': 'MerchantReturnPolicy',
            'applicableCountry': 'CA',
            'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
            'merchantReturnDays': 30,
            'returnMethod': 'https://schema.org/ReturnByMail',
            'returnFees': 'https://schema.org/FreeReturn'
          },
          'shippingDetails': shippingDetailsSchema
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.95',
          'reviewCount': '194',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        'mainEntity': [
          ...(matchedUseCase
            ? matchedUseCase.faqs.map(f => ({
                '@type': 'Question',
                'name': f.question,
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': f.answer
                }
              }))
            : []),
          {
            '@type': 'Question',
            'name': 'How much does shipping cost across Canada?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': enableFreeShipping
                ? `We offer 100% FREE tracked shipping across Canada on all orders!`
                : `We offer fast, tracked shipping across Canada. Orders of $${freeShippingThreshold.toFixed(2)} CAD or more automatically receive 100% FREE tracked shipping! Standard Canada-wide shipping is $${shippingRate.toFixed(2)} CAD for smaller orders, and local ${localCity}, BC delivery/pickup is available for just $${kamloopsRate.toFixed(2)} CAD.`
            }
          },
          {
            '@type': 'Question',
            'name': 'What are your bulk discount pricing tiers?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': `Our custom 2.5″ × 2.5″ square magnets start at $${basePrice.toFixed(2)} CAD each. We offer automatic volume bulk discounts that scale all the way down to $${lowestPrice.toFixed(2)} CAD per magnet (${tiersDescription}). No coupon code is required—volume discounts apply automatically in your cart.`
            }
          },
          {
            '@type': 'Question',
            'name': 'What are the dimensions and materials of each custom magnet?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Every magnet measures 2.5 inches × 2.5 inches (63.5 mm × 63.5 mm) with a solid steel shell core, high-strength full-coverage magnetic backing, vivid high-resolution photo printing, and a smooth, wipe-clean high-gloss Mylar finish.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Can I design magnets directly on my mobile phone?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes! Our interactive web studio is fully mobile-optimized. You can take live photos using our in-app viewfinder, upload photos from your smartphone camera roll, crop, apply photo filters, add personalized text with dozens of curated fonts and colors, and view real-time 3D mockups before ordering.'
            }
          },
          {
            '@type': 'Question',
            'name': 'How does the school and sports team fundraising program work?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Schools, minor sports teams, and non-profits can launch a fundraiser with zero upfront cost or leftover inventory. Your group receives a unique promo code, families order custom keepsakes online, and your organization earns up to 30% direct payout on every order with transparent participant tracking.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Where are SquareMagnets.ca magnets manufactured and shipped from?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Every square magnet is handcrafted with care at our print studio in Kamloops, British Columbia, Canada, and shipped coast-to-coast across all Canadian provinces and territories.'
            }
          }
        ]
      }
    ]
  };
}

/**
 * Client-Side React Hook: Dynamically synchronizes document.title,
 * meta tags, OpenGraph attributes, canonical link, and JSON-LD structured data
 * as the user navigates routes and as pricing updates from the admin.
 */
export function useRouteSeo({
  activeTab,
  dashboardTab,
  selectedNewsSlug,
  newsTitle,
  newsSummary,
  pricing
}: {
  activeTab: string;
  dashboardTab?: string;
  selectedNewsSlug?: string | null;
  newsTitle?: string | null;
  newsSummary?: string | null;
  pricing?: Partial<PricingConfig> | null;
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const seo = getRouteSeo(activeTab, {
      selectedNewsSlug,
      newsTitle,
      newsSummary,
      pricing,
      dashboardTab
    });

    // 1. Update Title
    document.title = seo.title;

    // Helper to safely set or create a <meta> element
    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let elem = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!elem) {
        elem = document.createElement('meta');
        elem.setAttribute(attr, key);
        document.head.appendChild(elem);
      }
      elem.setAttribute('content', content);
    };

    // 2. Standard Meta Tags
    setMeta('name', 'description', seo.description);
    setMeta('name', 'keywords', seo.keywords);

    // 3. Open Graph Tags
    setMeta('property', 'og:title', seo.ogTitle);
    setMeta('property', 'og:description', seo.ogDescription);
    setMeta('property', 'og:url', `${DEFAULT_ORIGIN}${seo.canonicalPath}`);
    setMeta('property', 'og:image', seo.ogImage);

    // 4. Twitter Card Tags
    setMeta('name', 'twitter:card', seo.twitterCard);
    setMeta('name', 'twitter:title', seo.ogTitle);
    setMeta('name', 'twitter:description', seo.ogDescription);
    setMeta('name', 'twitter:image', seo.ogImage);

    // 5. Canonical & hreflang Links
    const canonicalHref = `${DEFAULT_ORIGIN}${seo.canonicalPath}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);

    const setHreflang = (lang: string, href: string) => {
      let link = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    setHreflang('en-ca', canonicalHref);
    setHreflang('x-default', canonicalHref);

    // 6. Dynamic JSON-LD Structured Data (preserve server-rendered BlogPosting on /news/:slug if present)
    const graph = buildJsonLdGraph(pricing, seo.canonicalPath);
    let scriptTag = document.getElementById('json-ld-seo-graph') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement | null;
      if (scriptTag) {
        scriptTag.id = 'json-ld-seo-graph';
      } else {
        scriptTag = document.createElement('script');
        scriptTag.id = 'json-ld-seo-graph';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
    } else if (seo.canonicalPath.startsWith('/news/') && scriptTag.textContent) {
      try {
        const existing = JSON.parse(scriptTag.textContent);
        const existingBlogPosting = Array.isArray(existing?.['@graph'])
          ? existing['@graph'].find((n: any) => n?.['@type'] === 'BlogPosting')
          : null;
        if (existingBlogPosting) {
          (graph['@graph'] as any[]).push(existingBlogPosting);
        }
      } catch (e) {
        // ignore parse error
      }
    }
    scriptTag.textContent = JSON.stringify(graph, null, 2);

  }, [activeTab, dashboardTab, selectedNewsSlug, newsTitle, newsSummary, pricing]);
}
